// A card with a compact header: title left, a slot right. Phone padding (16),
// nothing wider than the column, never a horizontal scroll.
import type { ReactNode } from "react";
import { cn } from "@aiw/ui";

export function Panel({ title, right, children, className, bodyClassName }: { title?: ReactNode; right?: ReactNode; children: ReactNode; className?: string; bodyClassName?: string }) {
  return (
    <section className={cn("overflow-hidden rounded-lg border bg-card text-card-foreground", className)}>
      {(title || right) && (
        <header className="flex min-h-[44px] flex-wrap items-center gap-2 border-b px-4 py-2">
          {title && <h2 className="font-display text-[15px] font-semibold leading-tight">{title}</h2>}
          {right && <div className="ml-auto flex items-center gap-2 text-micro text-muted-foreground">{right}</div>}
        </header>
      )}
      <div className={cn("p-4", bodyClassName)}>{children}</div>
    </section>
  );
}

/** dt/dd pairs, one per line on the phone. */
export function KV({ rows }: { rows: { k: ReactNode; v: ReactNode }[] }) {
  return (
    <dl className="grid grid-cols-[minmax(88px,auto)_1fr] gap-x-3 gap-y-2 text-sm">
      {rows.map((r, i) => (
        <div key={i} className="contents">
          <dt className="text-muted-foreground">{r.k}</dt>
          <dd className="min-w-0 break-words">{r.v}</dd>
        </div>
      ))}
    </dl>
  );
}

export type Tone = "info" | "ok" | "warn" | "bad" | "plain";
const TONES: Record<Tone, string> = {
  info: "border-primary/30 bg-accent text-accent-foreground",
  ok: "border-success/40 bg-success/10 text-success",
  warn: "border-warning/40 bg-warning/10 text-warning",
  bad: "border-destructive/40 bg-destructive/10 text-destructive",
  plain: "border-border bg-muted text-muted-foreground",
};
export function Pill({ tone = "plain", children, className }: { tone?: Tone; children: ReactNode; className?: string }) {
  return <span className={cn("inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-micro font-medium leading-5", TONES[tone], className)}>{children}</span>;
}

export function Chip({ children, mono }: { children: ReactNode; mono?: boolean }) {
  return <span className={cn("inline-flex items-center rounded-md border bg-background px-2 py-0.5 text-micro leading-5", mono && "font-mono")}>{children}</span>;
}
